import { useDeferredValue, useEffect, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { GET_USER_ACTIONS, GET_AUDIT_LOG, GET_RUNTIME_LOGS, GET_USERS } from "@/graphql/queries";
import { useApp } from "@/context/AppContext";
import { mapToSnakeCase } from "@/graphql/mappers";

type Tab = "activity" | "audit" | "system";

interface ActionRow {
  id: number;
  ts: string;
  user_id: string;
  user_role: string;
  project_id: string;
  action: string;
  entity_type: string | null;
  entity_id: string | null;
  summary: string;
  details: string | null;
}

interface AuditRow {
  id: number;
  ts: string;
  user_id: string;
  user_role: string;
  active_project: string;
  method: string;
  path: string;
  request_body: string | null;
  response_status: number | null;
  response_body: string | null;
  duration_ms: number | null;
}

interface RuntimeLogRow {
  id: number;
  ts: string;
  level: string;
  logger: string;
  message: string;
}

function formatTs(ts: string): string {
  if (!ts) return "";
  const d = new Date(ts);
  return d.toLocaleString();
}

function exportToCsv(filename: string, headers: string[], rows: string[][]) {
  const escape = (s: string) => `"${String(s ?? "").replace(/"/g, '""')}"`;
  const lines = [headers.map(escape).join(",")];
  for (const row of rows) {
    lines.push(row.map(escape).join(","));
  }
  const blob = new Blob([lines.join("\n")], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}

export function AuditLogReport() {
  const { setCurrentView } = useApp();
  const [tab, setTab] = useState<Tab>("activity");
  const [selectedUser, setSelectedUser] = useState("");
  const [limit, setLimit] = useState(200);
  const [search, setSearch] = useState("");
  const deferredSearch = useDeferredValue(search.trim().toLowerCase());

  const { data: usersRaw } = useQuery<any>(GET_USERS);
  const usersList: { id: string; name: string }[] = usersRaw?.users?.users ?? [];

  const variables = {
    user: selectedUser || undefined,
    limit: tab === "system" ? 10000 : limit,
  };

  const { data: actionsRaw, loading: actionsLoading, error: actionsError } = useQuery<any>(GET_USER_ACTIONS, {
    variables,
    skip: tab !== "activity",
    fetchPolicy: "network-only",
  });

  const { data: auditRaw, loading: auditLoading, error: auditError } = useQuery<any>(GET_AUDIT_LOG, {
    variables,
    skip: tab !== "audit",
    fetchPolicy: "network-only",
  });

  const { data: systemRaw, loading: systemLoading, error: systemError } = useQuery<any>(GET_RUNTIME_LOGS, {
    variables: { limit: 10000 },
    skip: tab !== "system",
    fetchPolicy: "network-only",
    pollInterval: tab === "system" ? 5000 : 0,
  });

  const actions: ActionRow[] = actionsRaw ? (actionsRaw.userActions || []).map(mapToSnakeCase) : [];
  const auditEntries: AuditRow[] = auditRaw ? (auditRaw.auditLog || []).map(mapToSnakeCase) : [];
  const [lastRuntimeLogs, setLastRuntimeLogs] = useState<RuntimeLogRow[]>([]);
  const receivedRuntimeLogs: RuntimeLogRow[] | undefined = systemRaw?.runtimeLogs;
  useEffect(() => {
    if (receivedRuntimeLogs?.length) setLastRuntimeLogs(receivedRuntimeLogs);
  }, [receivedRuntimeLogs]);
  const runtimeLogs = receivedRuntimeLogs?.length ? receivedRuntimeLogs : lastRuntimeLogs;
  const systemEntries = deferredSearch
    ? runtimeLogs.filter(entry => Object.values(entry).some(value =>
        String(value ?? "").toLowerCase().includes(deferredSearch),
      ))
    : runtimeLogs;

  const loading = tab === "activity" ? actionsLoading : tab === "audit" ? auditLoading : systemLoading;
  const error = tab === "activity" ? actionsError : tab === "audit" ? auditError : systemError;

  const handleExport = () => {
    if (tab === "activity") {
      exportToCsv(
        `activity-log${selectedUser ? `-${selectedUser}` : ""}.csv`,
        ["Timestamp", "User", "Role", "Project", "Action", "Entity Type", "Entity ID", "Summary", "Details"],
        actions.map(a => [
          formatTs(a.ts), a.user_id, a.user_role, a.project_id,
          a.action, a.entity_type || "", a.entity_id || "", a.summary, a.details || "",
        ]),
      );
    } else if (tab === "audit") {
      exportToCsv(
        `audit-log${selectedUser ? `-${selectedUser}` : ""}.csv`,
        ["Timestamp", "User", "Role", "Project", "Method", "Path", "Status", "Duration (ms)", "Request Body", "Response Body"],
        auditEntries.map(a => [
          formatTs(a.ts), a.user_id, a.user_role, a.active_project,
          a.method, a.path, String(a.response_status ?? ""),
          String(a.duration_ms ?? ""), a.request_body || "", a.response_body || "",
        ]),
      );
    } else {
      exportToCsv(
        "system-logs.csv",
        ["Timestamp", "Level", "Logger", "Message"],
        systemEntries.map(entry => [formatTs(entry.ts), entry.level, entry.logger, entry.message]),
      );
    }
  };

  const tabCls = (t: Tab) =>
    `px-3 py-1.5 text-xs font-semibold cursor-pointer border-b-2 transition-colors ${
      tab === t
        ? "border-accent text-accent"
        : "border-transparent text-text2 hover:text-text"
    }`;

  return (
    <div className="flex-1 px-7 py-6 overflow-y-auto" data-testid="audit-log-view">
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <button
          className="text-text2 hover:text-text text-xs bg-transparent border-none cursor-pointer"
          aria-label="Back to process"
          onClick={() => setCurrentView("process")}
        >
          &larr; Back
        </button>
        <h1 className="text-xl font-semibold text-text">Audit Log Report</h1>
      </div>

      {/* Filters */}
      <div className="flex items-center gap-3 mb-4">
        <select
          value={selectedUser}
          onChange={e => setSelectedUser(e.target.value)}
          className="px-2 py-1 bg-bg border border-border rounded-default text-text text-[11px] outline-none min-w-[160px] h-7 focus:border-accent"
          aria-label="Filter by user"
        >
          <option value="">All users</option>
          {usersList.map(u => (
            <option key={u.id} value={u.id}>{u.name || u.id}</option>
          ))}
        </select>

        <select
          value={limit}
          onChange={e => setLimit(Number(e.target.value))}
          className="px-2 py-1 bg-bg border border-border rounded-default text-text text-[11px] outline-none min-w-[80px] h-7 focus:border-accent"
          aria-label="Row limit"
        >
          <option value={100}>100 rows</option>
          <option value={200}>200 rows</option>
          <option value={500}>500 rows</option>
          <option value={1000}>1000 rows</option>
          <option value={10000}>10,000 rows</option>
        </select>

        {tab === "system" && (
          <input
            type="search"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search system logs"
            aria-label="Search system logs"
            className="px-2 py-1 bg-bg border border-border rounded-default text-text text-[11px] outline-none min-w-[220px] h-7 focus:border-accent"
          />
        )}

        <button
          className="px-3 py-1.5 border border-accent rounded-default bg-accent text-white text-xs cursor-pointer font-semibold hover:bg-accent2 hover:border-accent2 transition-colors"
          onClick={handleExport}
          disabled={loading}
        >
          Export CSV
        </button>
      </div>

      {/* Tabs */}
      <div className="flex border-b border-border mb-4">
        <button className={tabCls("activity")} onClick={() => setTab("activity")}>
          Activity Log ({actions.length})
        </button>
        <button className={tabCls("audit")} onClick={() => setTab("audit")}>
          Request Audit Log ({auditEntries.length})
        </button>
        <button className={tabCls("system")} onClick={() => setTab("system")}>
          Recent System Logs ({tab === "system" ? systemEntries.length : ""})
        </button>
      </div>

      {loading && <div className="text-sm text-text2 py-4">Loading...</div>}

      {error && <div className="text-sm text-red py-4">Error: {error.message}</div>}

      {/* Activity Log Table */}
      {tab === "activity" && !loading && (
        <div className="bg-bg2 border border-border rounded-default overflow-hidden">
          <table className="w-full border-collapse text-xs table-fixed">
            <thead>
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[150px]">Timestamp</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[120px]">User</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[80px]">Role</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[100px]">Action</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[80px]">Entity</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2">Summary</th>
              </tr>
            </thead>
            <tbody>
              {actions.map(a => (
                <tr key={a.id} className="border-b border-border/50 hover:bg-bg3">
                  <td className="px-3 py-2 text-text2 truncate">{formatTs(a.ts)}</td>
                  <td className="px-3 py-2 text-accent font-semibold truncate">{a.user_id}</td>
                  <td className="px-3 py-2 text-text2">{a.user_role}</td>
                  <td className="px-3 py-2 text-text">{a.action}</td>
                  <td className="px-3 py-2 text-text2 truncate">{a.entity_id || ""}</td>
                  <td className="px-3 py-2 text-text truncate">{a.summary}</td>
                </tr>
              ))}
              {actions.length === 0 && (
                <tr><td colSpan={6} className="text-center py-8 text-text2">No activity records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Audit Log Table */}
      {tab === "audit" && !loading && (
        <div className="bg-bg2 border border-border rounded-default overflow-hidden">
          <table className="w-full border-collapse text-xs table-fixed">
            <thead>
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[150px]">Timestamp</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[120px]">User</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[80px]">Project</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[60px]">Method</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[100px]">Path</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[60px]">Status</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[60px]">Time (ms)</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2">Request</th>
              </tr>
            </thead>
            <tbody>
              {auditEntries.map(a => (
                <tr key={a.id} className="border-b border-border/50 hover:bg-bg3">
                  <td className="px-3 py-2 text-text2 truncate">{formatTs(a.ts)}</td>
                  <td className="px-3 py-2 text-accent font-semibold truncate">{a.user_id}</td>
                  <td className="px-3 py-2 text-text2 truncate">{a.active_project}</td>
                  <td className="px-3 py-2 text-text">{a.method}</td>
                  <td className="px-3 py-2 text-text2 truncate">{a.path}</td>
                  <td className="px-3 py-2 text-text">{a.response_status}</td>
                  <td className="px-3 py-2 text-text2">{a.duration_ms != null ? a.duration_ms.toFixed(0) : ""}</td>
                  <td className="px-3 py-2 text-text2 truncate max-w-[300px]">{a.request_body || ""}</td>
                </tr>
              ))}
              {auditEntries.length === 0 && (
                <tr><td colSpan={8} className="text-center py-8 text-text2">No audit records found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {/* Recent System Logs Table */}
      {tab === "system" && !loading && (
        <div className="bg-bg2 border border-border rounded-default overflow-auto">
          <div className="px-3 py-2 text-[11px] text-text2 border-b border-border">
            Showing {systemEntries.length.toLocaleString()} of {runtimeLogs.length.toLocaleString()} recent records; refreshes every 5 seconds
          </div>
          <table className="w-full border-collapse text-xs table-fixed min-w-[1100px]">
            <thead>
              <tr className="border-b border-border">
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[150px]">Timestamp</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[90px]">Level</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2 w-[180px]">Logger</th>
                <th className="px-3 py-2 text-left font-semibold text-[10px] uppercase tracking-wide text-text2">Message</th>
              </tr>
            </thead>
            <tbody>
              {systemEntries.map(entry => (
                <tr key={entry.id} className="border-b border-border/50 hover:bg-bg3 align-top">
                  <td className="px-3 py-2 text-text2 truncate">{formatTs(entry.ts)}</td>
                  <td className="px-3 py-2 text-text font-semibold">{entry.level}</td>
                  <td className="px-3 py-2 text-accent truncate" title={entry.logger}>{entry.logger}</td>
                  <td className="px-3 py-2 text-text2 whitespace-pre-wrap break-words font-mono text-[10px]">{entry.message}</td>
                </tr>
              ))}
              {systemEntries.length === 0 && (
                <tr><td colSpan={4} className="text-center py-8 text-text2">No matching system logs found.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
